import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'helpers/languages/translations';
import { useRef } from 'react';
import { TInternationalCompanyGroup } from '../useInternationalCompanyGroup';
import { IOptionalInternationalCompanyGroup } from 'types';
import {
    useAddInternationalCompanyGroupMutation,
    useUpdateInternationalCompanyGroupMutation,
    useDeleteInternationalCompanyGroupMutation,
} from 'core/API/internationalCompanyGroupApi';

interface IProps {
    setIsModalOpen: TInternationalCompanyGroup['setIsModalOpen'];
    modalType: TInternationalCompanyGroup['modalType'];
    modalData: TInternationalCompanyGroup['modalData'];
    setModalData: TInternationalCompanyGroup['setModalData'];
    globalCompanyId: TInternationalCompanyGroup['globalCompanyId'];
    onDeleteOpen: TInternationalCompanyGroup['onDeleteOpen'];
}

const useModal = ({ modalType, setIsModalOpen, modalData, setModalData, globalCompanyId, onDeleteOpen }: IProps) => {
    const translate = useTranslation();
    const updateInitialRef = useRef(null);
    const deleteInitialRef = useRef(null);
    const toast = useToast();
    const [updateInternationalCompanyGroup, { isLoading: isUpdateInternationalCompanyGroupLoading }] =
        useUpdateInternationalCompanyGroupMutation();
    const [addInternationalCompanyGroup, { isLoading: isAddInternationalCompanyGroupLoading }] =
        useAddInternationalCompanyGroupMutation();

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSubmit = async () => {
        let resp = {};
        const formattedModalData = {
            user_ids: modalData.users.map((user) => user.id),
            company_group_ids: modalData.company_groups.map((company) => company.id),
            international_company_id: modalData.international_company?.id,
            name: modalData.name,
            globalCompanyId: globalCompanyId,
            internationalCompanyGroupId: modalData.internationalCompanyGroupId,
        };

        try {
            if (modalType === 'add') {
                resp = await addInternationalCompanyGroup(formattedModalData as any);
            } else {
                resp = await updateInternationalCompanyGroup(formattedModalData as any);
            }
            if ('error' in resp) {
                throw new Error();
            } else {
                toast({
                    title: translate('icg.modal.success'),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch (e) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
        handleCloseModal();
    };

    const handleDelete = async () => {
        try {
            onDeleteOpen();
        } catch (e) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
        handleCloseModal();
    };

    const handleChangeUserList = (id: number, type: 'add' | 'delete') => {
        const source = type === 'add' ? 'available_users' : 'users';
        const target = type === 'add' ? 'users' : 'available_users';

        const user = modalData[source].find((user) => user.id === id);

        if (user) {
            setModalData((prev: IOptionalInternationalCompanyGroup) => ({
                ...prev,
                [source]: prev[source].filter((user) => user.id !== id),
                [target]: [...prev[target], user],
            }));
        }
    };

    const handleChangeCompany = (id: number, type: 'add' | 'delete') => {
        const source = type === 'add' ? 'available_company_groups' : 'company_groups';
        const target = type === 'add' ? 'company_groups' : 'available_company_groups';

        const company = modalData[source].find((company) => company.id === id);

        if (company) {
            setModalData((prev: IOptionalInternationalCompanyGroup) => ({
                ...prev,
                [source]: prev[source].filter((company) => company.id !== id),
                [target]: [...prev[target], company],
            }));
        }
    };

    const handleChangeName = (name: string) => {
        setModalData((prev: IOptionalInternationalCompanyGroup) => ({ ...prev, name }));
    };

    return {
        isUpdateInternationalCompanyGroupLoading,
        isAddInternationalCompanyGroupLoading,
        handleCloseModal,
        handleSubmit,
        handleDelete,
        updateInitialRef,
        deleteInitialRef,
        handleChangeUserList,
        handleChangeCompany,
        handleChangeName,
    };
};

export default useModal;
export type TModalHooks = ReturnType<typeof useModal>;
